import React, { useEffect } from 'react';
import analytics from '../../services/analytics';
import separatorIcon from '../../assets/images/icons/separator.svg';

/**
 * OrderOnlineModal — premium popup with Pick Up (Clover) and Delivery (GrubHub / Uber Eats).
 *
 * Props:
 *   isOpen   — boolean, controls visibility
 *   onClose  — callback to hide the modal
 */

const ORDER_LINKS = {
    clover: 'https://www.clover.com/online-ordering/huq-up-little-elm',
    grubhub: 'https://www.grubhub.com/restaurant/huq-up-27052-e-university-dr-little-elm/14057784',
    ubereats: 'https://www.ubereats.com/store/huq-up-27052-e-university-dr-100/xGVX7nd_TcmpRkhJdnhY0Q',
};

function OrderOnlineModal({ isOpen, onClose }) {
    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    // Close on Escape key
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="order-modal-overlay" onClick={handleBackdropClick} role="dialog" aria-modal="true" aria-label="Order Online">
            <div className="order-modal">
                {/* Close button */}
                <button className="order-modal__close" onClick={onClose} aria-label="Close">
                    <span className="fal fa-times"></span>
                </button>

                {/* Header */}
                <div className="order-modal__header">
                    <span className="order-modal__header-accent">How would you like to order?</span>
                    <h3 className="order-modal__title">Order Online</h3>
                    <div className="order-modal__separator">
                        <img src={separatorIcon} alt="" />
                    </div>
                </div>

                {/* Options */}
                <div className="order-modal__options">
                    {/* ── Pick Up Card ── */}
                    <a
                        href={ORDER_LINKS.clover}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="order-modal__card order-modal__card--pickup"
                        onClick={() => analytics.trackEvent('order_online_click', { event_label: 'clover_pickup' })}
                    >
                        <div className="order-modal__card-icon">
                            <span className="fas fa-shopping-bag"></span>
                        </div>
                        <div className="order-modal__card-content">
                            <h4 className="order-modal__card-title">Pick Up</h4>
                            <p className="order-modal__card-desc">Order ahead & pick up at our restaurant</p>
                        </div>
                        <span className="order-modal__card-arrow fas fa-chevron-right"></span>
                    </a>

                    {/* ── Delivery Cards ── */}
                    <div className="order-modal__delivery-label">
                        <span></span>
                        <p>Delivery</p>
                        <span></span>
                    </div>

                    <div className="order-modal__delivery-row">
                        <a
                            href={ORDER_LINKS.grubhub}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="order-modal__card order-modal__card--grubhub"
                            onClick={() => analytics.trackEvent('order_online_click', { event_label: 'grubhub_delivery' })}
                        >
                            <div className="order-modal__card-icon">
                                <span className="fas fa-car"></span>
                            </div>
                            <div className="order-modal__card-content">
                                <h4 className="order-modal__card-title">GrubHub</h4>
                            </div>
                            <span className="order-modal__card-arrow fas fa-external-link-alt"></span>
                        </a>

                        <a
                            href={ORDER_LINKS.ubereats}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="order-modal__card order-modal__card--ubereats"
                            onClick={() => analytics.trackEvent('order_online_click', { event_label: 'ubereats_delivery' })}
                        >
                            <div className="order-modal__card-icon">
                                <span className="fas fa-car"></span>
                            </div>
                            <div className="order-modal__card-content">
                                <h4 className="order-modal__card-title">Uber Eats</h4>
                            </div>
                            <span className="order-modal__card-arrow fas fa-external-link-alt"></span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OrderOnlineModal;
